import React, { createRef } from "react";
import { compose } from "redux";
import { connect } from "react-redux";
import PropTypes from "prop-types";
import { ReviewsSort } from "rtr-constants";
import { productPropType, userReviewSummaryPropType } from "components/propTypes";
import { isAdmin, userDataPropType, withUserData } from "components/source/hoc/with-user-data";
import { hasRented, withUserReviewSummary } from "components/layout/UserReviewSummaryContext";
import Review from "./review";
import ReviewsPagination from "./reviews-pagination";
import ReviewsHeader from "./reviews-header";
import reviewsActions from "actions/reviews-actions";
import ReviewModal from "../universal_pdp/review-modal";

export class ReviewsComponent extends React.Component {
  static propTypes = {
    count: PropTypes.number,
    coverPhotos: PropTypes.array,
    data: PropTypes.arrayOf(
      PropTypes.shape({
        moment: PropTypes.object,
      })
    ),
    momentsWithPhotos: PropTypes.arrayOf(
      PropTypes.shape({
        moment: PropTypes.object,
      })
    ),
    dispatch: PropTypes.func.isRequired,
    page: PropTypes.number,
    perPage: PropTypes.number,
    product: productPropType,
    shouldLoadReviews: PropTypes.bool,
    sort: PropTypes.string,
    summary: PropTypes.object,
    userData: userDataPropType,
    userReviewSummary: userReviewSummaryPropType,
    writingReview: PropTypes.bool,
    filteredCount: PropTypes.number,
  };

  constructor(props) {
    super(props);

    this.componentWrapperRef = createRef();
  }

  // I would prefer to move this to either a Context or the Redux Store and do
  // the data-fetching at a higher level. I'm not looking to re-write all of
  // this now, so we've got a bit of a hybrid
  state = {
    selectedSizes: [],
    page: 1,
    sort: null,
    reviewModalReviewId: null,
    reviewModalOpen: false,
  };

  componentDidMount() {
    const { data, page, shouldLoadReviews, sort = ReviewsSort.FEATURED } = this.props;

    this.setState({ page: page, sort: sort });

    if (this.props.writingReview && this.isReviewableProduct()) {
      this.triggerReviewModal();
    }

    // remove guard and fetch once on Storefront.Next
    if (!shouldLoadReviews) return;

    if (!Array.isArray(data) || data.length <= 0) {
      // should probably set sort to Most Popular when userProfile is blank
      const params = { page, sort };

      this.requestReviews(params);
    }
  }

  filterReviewsBySize = selectedSizes => {
    if (this.state.page > 1) {
      this.setState({
        selectedSizes: selectedSizes,
      });
      this.changePage(1, selectedSizes);
    } else {
      const params = { sort: this.state.sort, page: this.state.page, sizes: selectedSizes };
      this.requestReviews(params);
      this.setState({
        selectedSizes: selectedSizes,
      });
    }
  };

  sortReviews = e => {
    const filter = e.target.value;
    const params = { sort: filter, page: this.state.page, sizes: this.state.selectedSizes };
    this.requestReviews(params);
    this.setState({
      sort: filter,
    });
  };

  isReviewableProduct() {
    const { userData, userReviewSummary } = this.props;

    return hasRented(userReviewSummary, this.getStyleName()) || isAdmin(userData);
  }

  changePage = (page, selectedSizes = null) => {
    const params = { page: page, sort: this.props.sort, sizes: selectedSizes || this.state.selectedSizes };

    this.setState({ page: page });

    this.requestReviews(params);

    this.componentWrapperRef.current.scrollIntoView();
  };

  requestReviews = params => {
    this.props.dispatch(reviewsActions.requestReviews(this.getStyleName(), params));
  };

  triggerReviewModal = e => {
    if (e) {
      e.preventDefault();
    }

    const popupSource = e ? "pdp-banner" : "url-driven";
    this.props.dispatch(reviewsActions.displayReviewUploadModal({ styleName: this.getStyleName(), popupSource }));
  };

  submitComment = (reviewId, commentText) => {
    this.props.dispatch(reviewsActions.submitComment(reviewId, commentText));
  };

  getStyleName = () => {
    const { product } = this.props;
    return (product?.id || product?.productId) ?? "";
  };

  render() {
    let reviewsPagination;
    const { page, filteredCount, count, perPage, sort, summary, product, data } = this.props;

    if (filteredCount > perPage) {
      reviewsPagination = (
        <ReviewsPagination
          page={this.state.page}
          pagingCallback={this.changePage}
          count={filteredCount}
          perPage={perPage}
        />
      );
    }

    // NW [EXPLANATION] 1/21/20: the first cover photo id will be used to support forward/back buttons in the moment lightbox
    const firstPhotoId = this.props.coverPhotos?.[0]?.id ?? null;
    const coverPhotos = data ? data.filter(review => review?.moment?.photos) : [];

    return (
      <div className="reviews-desktop" ref={this.componentWrapperRef}>
        <ReviewsHeader
          coverPhotos={coverPhotos}
          count={count}
          page={page}
          product={product}
          requestReviews={this.requestReviews}
          summary={summary}
          sort={this.state.sort}
          styleName={this.getStyleName()}
          triggerReviewModal={this.triggerReviewModal}
          filterReviewsBySize={this.filterReviewsBySize}
          selectedSizes={this.state.selectedSizes}
          sortReviews={this.sortReviews}
        />
        <div className="reviews-column">
          <div style={{ clear: "both" }}></div>
          <div className="reviews">
            {Array.isArray(data) &&
              data.map(review => {
                return (
                  <Review
                    firstPhotoId={firstPhotoId}
                    key={review?.reviewId}
                    product={product}
                    review={review}
                    sort={sort}
                    submitComment={this.submitComment}
                    newAPIData={true}
                    setModalOpen={open => this.setState({ reviewModalOpen: open })}
                    setReviewForModal={reviewId => this.setState({ reviewModalReviewId: reviewId })}
                  />
                );
              })}
            {reviewsPagination}
          </div>
        </div>
        {this.props.momentsWithPhotos?.length > 0 && this.state.reviewModalOpen && (
          <ReviewModal
            productDisplayName={this.props.product?.displayName}
            productId={this.props.product?.id}
            reviewId={this.state.reviewModalReviewId}
            setIsOpen={open => this.setState({ reviewModalOpen: open })}
            isOpen={this.state.reviewModalOpen}
          />
        )}
      </div>
    );
  }
}

const mapStateToProps = state => ({
  filteredCount: state?.reviews?.filteredCount,
  momentsWithPhotos: state?.reviews?.reviewsWithPhotos,
});

export default compose(withUserData, withUserReviewSummary(), connect(mapStateToProps))(ReviewsComponent);
